import { zonoApi } from '../api/zonoApi';
import {
  ConversationListRequest,
  ConversationListResponse,
  ConversationMessagesRequest,
  ConversationMessagesResponse,
  ConversationStatusGroup,
  ConversationStatusResponse,
  MessageMediaRequest,
  PublishMessagePayload,
  UpdateConversationAssigneeRequest,
  UpdateConversationResponse,
  UpdateConversationStatusRequest,
  UpdateConversationTagsRequest,
  UpdateConversationTagsResponse,
  WaConfigData,
  MediaRequest,
  SingleMessageRequest,
  SingleMessageResponse,
  RedingtonMediaRequest,
  GetConversationQueryTypesResponse,
  GetConversationQueryTypesRequest,
  GetConversationQueryDetailsRequest,
  GetConversationQueryDetailsResponse,
  MessageHistoryRequest,
  MessageHistoryResponse,
} from './conversations-model';
import { loadConversationStatusMap } from './conversations-slice';

export const conversationApi = zonoApi.injectEndpoints({
  endpoints: (build) => ({
    getSignUrl: build.mutation<{ presignedUrl: string }, MediaRequest>({
      query(MediaRequest) {
        return {
          url: `sign/url`,
          method: 'POST',
          body: MediaRequest,
        };
      },
    }),
    getConversations: build.query<
      ConversationListResponse,
      ConversationListRequest
    >({
      query({
        sellerWorkspaceId,
        tags,
        teamMemberIds,
        channelTypes,
        channelSubTypes,
        ...args
      }) {
        const searchParams = new URLSearchParams();
        searchParams.append('sellerWorkspaceId', sellerWorkspaceId);
        tags?.forEach((id) => searchParams.append('tags', id));
        teamMemberIds?.forEach((id) => searchParams.append('assigneeId', id));
        channelTypes?.forEach((val) =>
          searchParams.append('channelTypes[]', val)
        );
        channelSubTypes?.forEach((val) =>
          searchParams.append('channelSubTypes[]', val)
        );
        Object.entries(args).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            searchParams.append(key, String(value));
          }
        });
        searchParams.append('isReturnNewSemanticConversations', 'true');

        return {
          url: `/hub/communication-v2/api/conversations/${sellerWorkspaceId}?${searchParams}`,
          method: 'GET',
          headers: { 'Cache-control': 'no-cache' },
        };
      },
    }),
    updateStatus: build.mutation<
      UpdateConversationResponse,
      UpdateConversationStatusRequest
    >({
      query({ conversationId, sellerWorkspaceId, status }) {
        return {
          url: `/hub/communication-v2/api/conversations/${conversationId}/status`,
          method: 'PUT',
          params: { sellerWorkspaceId },
          body: { status, isReturnNewSemanticConversations: true },
        };
      },
    }),
    updateAssignee: build.mutation<
      UpdateConversationResponse,
      UpdateConversationAssigneeRequest
    >({
      query({ conversationId, sellerWorkspaceId, teamMemberId }) {
        return {
          url: `/hub/communication-v2/api/conversations/${conversationId}/assign`,
          method: 'PUT',
          params: { sellerWorkspaceId },
          body: {
            isReturnNewSemanticConversations: true,
            teamMemberId,
          },
        };
      },
    }),
    getConversationStatuses: build.query<
      ConversationStatusResponse,
      { sellerWorkspaceId: string }
    >({
      query({ sellerWorkspaceId }) {
        return {
          url: `/hub/communication-v2/api/conversation-status/${sellerWorkspaceId}`,
          method: 'GET',
          params: { sellerWorkspaceId },
        };
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        queryFulfilled
          .then((response) => {
            const { data } = response;
            const statusMap: Record<ConversationStatusGroup, string[]> = {
              [ConversationStatusGroup.OPEN]: [],
              [ConversationStatusGroup.INPROGRESS]: [],
              [ConversationStatusGroup.CLOSED]: [],
            };
            data.statuses.forEach(({ category, status }) => {
              statusMap[category].push(status);
            });
            dispatch(loadConversationStatusMap(statusMap));
          })
          .catch((error) =>
            console.log('getConversationStatuses error', error)
          );
      },
    }),
    getConversationMessage: build.query<
      ConversationMessagesResponse,
      ConversationMessagesRequest
    >({
      query: ({ conversationId, sellerWorkspaceId }) => ({
        url: `/hub/communication-v2/api/conversations/detail/${conversationId}`,
        method: 'GET',
        params: {
          sellerWorkspaceId,
          isReturnNewSemanticConversations: true,
        },
      }),
    }),
    getMessageMediaUrl: build.mutation<string, MessageMediaRequest>({
      query(arg) {
        return {
          url: `sign/url`,
          method: 'POST',
          body: { destination: 'channels', ...arg },
        };
      },
      transformResponse: (response: { presignedUrl: string }) => {
        return response.presignedUrl;
      },
    }),
    publishMessage: build.mutation<void, PublishMessagePayload>({
      query(arg) {
        return {
          url: 'hub/communication-v2/api/communicate/send',
          method: 'POST',
          body: { ...arg },
        };
      },
    }),
    getWhatsappConfig: build.query<
      WaConfigData[],
      { sellerWorkspaceId: string }
    >({
      query({ sellerWorkspaceId }) {
        return {
          url: `hub/orgs/api/whatsapp-config?sellerWorkspaceId=${sellerWorkspaceId}`,
          method: 'GET',
        };
      },
    }),
    removeAssignee: build.mutation<
      UpdateConversationResponse,
      UpdateConversationAssigneeRequest
    >({
      query({ conversationId, sellerWorkspaceId, teamMemberId }) {
        return {
          url: `/hub/communication-v2/api/conversations/${conversationId}/unassign`,
          method: 'PUT',
          params: { sellerWorkspaceId },
          body: {
            isReturnNewSemanticConversations: true,
            teamMemberId,
          },
        };
      },
    }),
    updateConversationTags: build.mutation<
      UpdateConversationTagsResponse,
      UpdateConversationTagsRequest
    >({
      query(args) {
        const { conversationId, workspaceId, tags } = args;
        return {
          url: `/hub/communication-v2/api/conversations/tags/${conversationId}`,
          method: 'PUT',
          body: {
            tags,
            workspaceId,
            sellerWorkspaceId: workspaceId,
            isReturnNewSemanticConversations: true,
          },
        };
      },
    }),
    getSingleMessage: build.query<SingleMessageResponse, SingleMessageRequest>({
      query: ({ messageId, ...params }) => ({
        url: `hub/communication-v2/api/communicate/message/${messageId}`,
        method: 'GET',
        params,
      }),
    }),
    getMediaFromRedington: build.query<{ url: string }, RedingtonMediaRequest>({
      query: ({ id, waConfigId, workspaceId }) => ({
        url: `hub/orgs/api/whatsapp-config/get-media/${id}/${waConfigId}`,
        method: 'GET',
        params: { sellerWorkspaceId: workspaceId },
      }),
    }),
    getConversationQueryTypes: build.query<
      GetConversationQueryTypesResponse,
      GetConversationQueryTypesRequest
    >({
      query(args) {
        return {
          url: `/hub/communication-v2/api/conversation-threads/query-types/${args.params.sellerWorkspaceId}`,
          method: 'GET',
          params: args.params,
        };
      },
    }),
    getConversationQueryDetails: build.query<
      GetConversationQueryDetailsResponse,
      GetConversationQueryDetailsRequest
    >({
      query(args) {
        return {
          url: `/hub/communication-v2/api/conversation-threads/${args.params.sellerWorkspaceId}`,
          method: 'GET',
          params: args.params,
        };
      },
    }),
    getMessageHistory: build.query<
      MessageHistoryResponse,
      MessageHistoryRequest
    >({
      query(args) {
        const { workspaceId } = args;
        return {
          url: `hub/communication-v2/api/communicate/messages`,
          method: 'GET',
          params: { ...args, sellerWorkspaceId: workspaceId },
        };
      },
    }),
  }),
});

export const {
  useLazyGetConversationsQuery,
  useUpdateAssigneeMutation,
  useUpdateStatusMutation,
  useLazyGetConversationStatusesQuery,
  useLazyGetConversationMessageQuery,
  useGetMessageMediaUrlMutation,
  usePublishMessageMutation,
  useLazyGetWhatsappConfigQuery,
  useRemoveAssigneeMutation,
  useUpdateConversationTagsMutation,
  useGetSignUrlMutation,
  useLazyGetSingleMessageQuery,
  useLazyGetMediaFromRedingtonQuery,
  useLazyGetConversationQueryTypesQuery,
  useLazyGetConversationQueryDetailsQuery,
  useLazyGetMessageHistoryQuery,
} = conversationApi;
